import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useMediaQuery, useTheme } from "@mui/material";
import { type FC } from "react";

import { MarketAmountField } from "~/feature/event/components/MarketCalculator/components/MarketAmountField";
import { MarketCalculatorQuestion } from "~/feature/event/components/MarketCalculator/components/MarketCalculatorQuestion";
import { MarketCountDown } from "~/feature/event/components/MarketCalculator/components/MarketCountDown";
import { TradeOnLine } from "~/feature/event/components/MarketCalculator/components/TradeOn";
import { useMarketCalculator } from "~/feature/event/providers/MarketCalculatorProvider";
import { type IAggregatedEvent, MarketStatus } from "~/types/event";

import * as Styled from "./MarketCalculatorLine.styles";
import { useMarketCalculatorLineCollapse } from "./useMarketCalculatorLineCollapse";
import { useMarketCalculatorLineSlider } from "./useMarketCalculatorLineSlider";

interface IMarketCalculatorLineProps {
  event: IAggregatedEvent;
}

export const MarketCalculatorLine: FC<IMarketCalculatorLineProps> = ({
  event,
}) => {
  const theme = useTheme();
  const isMobileBar = useMediaQuery(theme.breakpoints.down("md"));
  const { market, isCalculatorVisible } = useMarketCalculator();

  const {
    activeMarket,
    hasMultiple,
    sliderOrder,
    safeIdx,
    goPrev,
    goNext,
    selectIndex,
    swipeHandlers: sliderSwipeHandlers,
  } = useMarketCalculatorLineSlider();

  const { isCollapsed, collapse, expand, expandSwipeHandlers } =
    useMarketCalculatorLineCollapse();

  if (!market || isCalculatorVisible) return null;

  if (
    market.unifiedStatus === MarketStatus.CLOSED ||
    market.unifiedStatus === MarketStatus.RESOLVED ||
    market.bestAsk === 1 ||
    market.bestAsk === 0
  ) {
    return null;
  }

  if (!activeMarket) return null;

  const collapsedBar = (
    <Styled.CollapsedBar
      elevation={0}
      onClick={expand}
      data-bottom-dock="market-calculator"
      {...expandSwipeHandlers}
    >
      <Styled.MobileTopSection>
        <Styled.QuestionLine>
          <MarketCalculatorQuestion
            market={activeMarket}
            variant="line"
            singleMarket
          />
          <Styled.CollapseToggle
            aria-label="Expand calculator"
            aria-expanded={false}
            size="small"
            onClick={expand}
          >
            <KeyboardArrowUpIcon />
          </Styled.CollapseToggle>
        </Styled.QuestionLine>
        <MarketAmountField line hidePresets />
      </Styled.MobileTopSection>
    </Styled.CollapsedBar>
  );

  const expandedDrawer = (
    <Styled.MobileDrawer
      anchor="bottom"
      elevation={0}
      open={!isCollapsed}
      onOpen={expand}
      onClose={collapse}
      disableSwipeToOpen
      hideBackdrop
      ModalProps={{
        keepMounted: false,
        disableScrollLock: true,
        disableEnforceFocus: true,
        disableAutoFocus: true,
      }}
    >
      <Styled.MobileTopSection>
        <Styled.QuestionLine>
          <MarketCalculatorQuestion
            market={activeMarket}
            variant="line"
            singleMarket
          />
          <Styled.CollapseToggle
            aria-label="Collapse calculator"
            aria-expanded
            size="small"
            onClick={collapse}
          >
            <KeyboardArrowDownIcon />
          </Styled.CollapseToggle>
        </Styled.QuestionLine>
      </Styled.MobileTopSection>

      <Styled.CollapsibleContent>
        <Styled.MobileTopSection>
          <MarketAmountField line />
        </Styled.MobileTopSection>

        <Styled.SliderRow $hasArrows={hasMultiple}>
          {hasMultiple && (
            <Styled.SliderArrow
              aria-label="Previous market"
              size="small"
              onClick={goPrev}
            >
              <ChevronLeftIcon />
            </Styled.SliderArrow>
          )}
          <Styled.SliderViewport {...(hasMultiple ? sliderSwipeHandlers : {})}>
            <TradeOnLine market={activeMarket} event={event} singleCard />
          </Styled.SliderViewport>
          {hasMultiple && (
            <Styled.SliderArrow
              aria-label="Next market"
              size="small"
              onClick={goNext}
            >
              <ChevronRightIcon />
            </Styled.SliderArrow>
          )}
        </Styled.SliderRow>

        {hasMultiple && (
          <Styled.SliderDots>
            {sliderOrder.map((_, dotIdx) => (
              <Styled.SliderDot
                key={dotIdx}
                type="button"
                aria-label={`Market ${dotIdx + 1}`}
                $active={dotIdx === safeIdx}
                onClick={() => selectIndex(dotIdx)}
              />
            ))}
          </Styled.SliderDots>
        )}
      </Styled.CollapsibleContent>
    </Styled.MobileDrawer>
  );

  const desktopBar = (
    <Styled.Root elevation={0}>
      <Styled.TopWrapper>
        <Styled.CountdownSlot>
          <MarketCountDown market={market} hideLabel={true} />
        </Styled.CountdownSlot>
        <MarketCalculatorQuestion market={market} variant="line" singleMarket />
        <MarketAmountField line />
      </Styled.TopWrapper>
      <Styled.TradeCardsRow>
        <TradeOnLine market={market} event={event} />
      </Styled.TradeCardsRow>
    </Styled.Root>
  );

  if (isMobileBar) {
    return (
      <>
        {isCollapsed && collapsedBar}
        {expandedDrawer}
      </>
    );
  }

  return desktopBar;
};
